import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs, doc, getDoc, orderBy } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { getOrInitStock, calculateClosing } from "../utils/stockUtils";
import Layout from "../components/Layout";
import { getISTDate } from "../utils/dateUtils";
import * as XLSX from "xlsx";
import { FaFileExcel } from "react-icons/fa";

const RsmStockReports = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const level = searchParams.get("level") || "cnf";
    
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [skuMaster, setSkuMaster] = useState({});
    
    // Filters
    const [date, setDate] = useState(getISTDate());
    const [entities, setEntities] = useState({}); // Map of ID -> Name
    const [allDs, setAllDs] = useState([]); // Store DS with details for filtering
    const [distributors, setDistributors] = useState({}); // Map of ID -> Name for filter
    const [selectedDistributorId, setSelectedDistributorId] = useState("all");
    const [selectedEntityId, setSelectedEntityId] = useState("all");

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) { navigate("/"); return; }
            const snap = await getDoc(doc(db, "users", user.uid));
            if (snap.exists() && snap.data().role === "rsm") {
                setUserProfile(snap.data());
            } else {
                navigate("/");
            }
        });
        return () => unsub();
    }, [navigate]);

    useEffect(() => {
        if (userProfile) {
            loadBaseData();
        }
    }, [userProfile, level]);

    const loadBaseData = async () => {
        try {
            // Load SKUs
            const skuSnap = await getDocs(collection(db, "skuMaster"));
            const smap = {};
            skuSnap.forEach(d => {
                const data = d.data();
                const skuUpper = String(data.sku || data.Name || d.id).trim().toUpperCase();
                smap[skuUpper] = data;
            });
            setSkuMaster(smap);

            if (level === "distributor") {
                const distSnap = await getDocs(collection(db, "partyMaster"));
                const dMap = {};
                distSnap.forEach(d => dMap[d.id] = d.data().name || d.id);
                setEntities(dMap);
            } else if (level === "ds") {
                // Load Distributors for the parent filter
                const distSnap = await getDocs(collection(db, "partyMaster"));
                const dMap = {};
                distSnap.forEach(d => dMap[d.id] = d.data().name || d.id);
                setDistributors(dMap);

                const dsSnap = await getDocs(collection(db, "dsMaster"));
                const list = dsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                setAllDs(list);
                
                // Initial population of entities (all DS)
                const dsMap = {};
                list.forEach(d => {
                    dsMap[d.dsId || d.id] = d.name || d.dsId || d.id;
                });
                setEntities(dsMap);
            }
        } catch (err) {
            console.error("Error loading base data:", err);
        }
    };

    const loadStockData = async () => {
        setLoading(true);
        try {
            let collectionName = "cnf_stock_daily";
            if (level === "distributor") collectionName = "distributor_stock_daily";
            if (level === "ds") collectionName = "ds_stock_daily";

            let q;
            if (selectedEntityId === "all" || level === "cnf") {
                q = query(
                    collection(db, collectionName),
                    where("date", "==", date)
                );
            } else {
                const idField = level === "distributor" ? "distributorId" : "dsId";
                q = query(
                    collection(db, collectionName),
                    where("date", "==", date),
                    where(idField, "==", selectedEntityId)
                );
            }

            let results = [];
            if (level === "cnf") {
                const stockDoc = await getOrInitStock("cnf", userProfile.clientId, date, userProfile.clientId);
                results = [stockDoc];
            } else {
                const snap = await getDocs(q);
                let rawDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                const idField = level === "distributor" ? "distributorId" : "dsId";
                
                // For "all" view, ensure every entity is "Warmed Up" with carry-forward (C&F Logic)
                if (selectedEntityId === "all") {
                    const missingEids = Object.keys(entities).filter(eid => !rawDocs.some(d => d[idField] === eid));
                    
                    if (missingEids.length > 0) {
                        const repairedDocs = [];
                        const chunkSize = 10; // Batching for performance
                        for (let i = 0; i < missingEids.length; i += chunkSize) {
                            const chunk = missingEids.slice(i, i + chunkSize);
                            const chunkResults = await Promise.all(
                                chunk.map(eid => getOrInitStock(level, eid, date, userProfile.clientId))
                            );
                            repairedDocs.push(...chunkResults);
                        }
                        rawDocs = [...rawDocs, ...repairedDocs];
                    }

                    const mergedResults = [];
                    Object.keys(entities).forEach(eid => {
                        const existing = rawDocs.find(d => d[idField] === eid);
                        if (existing) {
                            mergedResults.push(existing);
                        } else {
                            // Safety fallback if repair failed
                            mergedResults.push({ [idField]: eid, inventory: {} });
                        }
                    });
                    results = mergedResults;
                } else {
                    const stockDoc = await getOrInitStock(level, selectedEntityId, date, userProfile.clientId);
                    results = [stockDoc];
                }
            }

            if (selectedEntityId !== "all" && level !== "cnf") {
                const idField = level === "distributor" ? "distributorId" : "dsId";
                results = results.filter(r => r[idField] === selectedEntityId);
            }

            // Transform nested inventory to flat rows
            const flatRows = [];
            results.forEach(docData => {
                const idField = level === "distributor" ? "distributorId" : "dsId";
                const entityId = docData[idField] || docData.id;
                const entityName = entities[entityId] || docData.name || entityId || "Hub";
                
                const inv = docData.inventory || {};
                
                // User Request: Force all SKUs from skuMaster to show in reports
                Object.entries(skuMaster).forEach(([skuUpper, m]) => {
                    const details = inv[skuUpper] || { opening: 0 };
                    
                    const opening = Number(details.opening || 0);
                    const received = Number(details.received || 0) + Number(details.purchase || 0) + Number(details.billed || 0) + Number(details.received_load || 0);
                    const sold = Number(details.sold || 0) + Number(details.sale || 0) + Number(details.despatched || 0);
                    const closing = opening + received - sold;

                    flatRows.push({
                        entity: entityName,
                        sku: skuUpper,
                        skuName: m.Name || skuUpper,
                        opening,
                        received,
                        sold,
                        closing
                    });
                });
            });

            setData(flatRows);
        } catch (err) {
            console.error("Error loading stock data:", err);
        } finally {
            setLoading(false);
        }
    };

    const exportToExcel = () => {
        if (data.length === 0) {
            alert("No data available to export");
            return;
        }

        const exportData = data.map(row => ({
            "Entity": row.entity,
            "SKU": row.sku,
            "Product Name": row.skuName,
            "Opening": row.opening,
            "Received": row.received,
            "Sold": row.sold,
            "Closing": row.closing
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Stock Ledger");
        XLSX.writeFile(wb, `Stock_Ledger_${level}_${date}.xlsx`);
    };

    // Handle dynamic DS filtering based on selected distributor
    useEffect(() => {
        if (level === "ds" && allDs.length > 0) {
            const filtered = selectedDistributorId === "all" 
                ? allDs 
                : allDs.filter(d => d.distributorId === selectedDistributorId);
            
            const dsMap = {};
            filtered.forEach(d => {
                dsMap[d.dsId || d.id] = d.name || d.dsId || d.id;
            });
            setEntities(dsMap);
            setSelectedEntityId("all"); // Reset individual DS filter
        }
    }, [selectedDistributorId, allDs, level]);

    useEffect(() => {
        if (userProfile && Object.keys(skuMaster).length > 0) {
            loadStockData();
        }
    }, [userProfile, level, date, selectedEntityId, skuMaster, entities]);

    const totals = useMemo(() => {
        return data.reduce((acc, row) => ({
            opening: acc.opening + row.opening,
            received: acc.received + row.received,
            sold: acc.sold + row.sold,
            closing: acc.closing + row.closing
        }), { opening: 0, received: 0, sold: 0, closing: 0 });
    }, [data]);

    const reportTitle = {
        "cnf": "2.1 C&F Stock Ledger",
        "distributor": "2.2 Distributor Stock Ledger",
        "ds": "2.3 DS Stock Ledger"
    }[level];

    return (
        <Layout>
            <div className="layout-content">
                <div style={{ marginBottom: 32, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                    <div>
                        <button onClick={() => navigate("/rsm-dashboard")} style={{ background: "none", border: "none", color: "var(--primary)", fontWeight: 800, cursor: "pointer", marginBottom: 12, padding: 0 }}>← BACK TO DASHBOARD</button>
                        <h1 style={{ fontSize: 32, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 16 }}>Inventory Intelligence</h1>
                        
                        <div style={{ display: "flex", gap: 8, background: "#f1f5f9", padding: 4, borderRadius: 12, width: "fit-content", marginBottom: 8 }}>
                            {[
                                { id: "cnf", label: "C&F STOCK" },
                                { id: "distributor", label: "DISTRIBUTOR STOCK" },
                                { id: "ds", label: "DS STOCK" }
                            ].map(tab => (
                                <div
                                    key={tab.id}
                                    onClick={() => navigate(`/rsm/stock-reports?level=${tab.id}`)}
                                    style={{
                                        padding: "10px 20px",
                                        borderRadius: 10,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        cursor: "pointer",
                                        transition: "all 0.2s ease",
                                        background: level === tab.id ? "white" : "transparent",
                                        color: level === tab.id ? "var(--primary)" : "var(--text-muted)",
                                        boxShadow: level === tab.id ? "0 4px 12px rgba(0,0,0,0.05)" : "none"
                                    }}
                                >
                                    {tab.label}
                                </div>
                            ))}
                        </div>
                    </div>
                    <button 
                        className="btn-secondary" 
                        onClick={exportToExcel}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 24px" }}
                    >
                        <FaFileExcel style={{ fontSize: 18 }} />
                        EXPORT TO EXCEL
                    </button>
                </div>

                <div className="premium-card" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20, marginBottom: 32 }}>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", display: "block", marginBottom: 6 }}>REPORT DATE</label>
                        <input type="date" className="form-input" value={date} onChange={e => setDate(e.target.value)} style={{ width: "100%" }} />
                    </div>
                    {level === "ds" && (
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", display: "block", marginBottom: 6 }}>FILTER BY DISTRIBUTOR</label>
                            <select className="form-input" value={selectedDistributorId} onChange={e => setSelectedDistributorId(e.target.value)} style={{ width: "100%" }}>
                                <option value="all">-- All Distributors --</option>
                                {Object.entries(distributors).map(([id, name]) => (
                                    <option key={id} value={id}>{name} ({id})</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {level !== "cnf" && (
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", display: "block", marginBottom: 6 }}>SELECT {level === "ds" ? "SALESMAN" : level.toUpperCase()}</label>
                            <select className="form-input" value={selectedEntityId} onChange={e => setSelectedEntityId(e.target.value)} style={{ width: "100%" }}>
                                <option value="all">-- All {level === "ds" ? "Salesmen" : level}s --</option>
                                {Object.entries(entities).map(([id, name]) => (
                                    <option key={id} value={id}>{name} ({id})</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
                    <table className="enterprise-table">
                        <thead>
                            <tr>
                                {level !== "cnf" && <th>Entity</th>}
                                <th>SKU</th>
                                <th style={{ textAlign: "right" }}>Opening</th>
                                <th style={{ textAlign: "right", color: "var(--success)" }}>Received (+)</th>
                                <th style={{ textAlign: "right", color: "var(--error)" }}>Sold (-)</th>
                                <th style={{ textAlign: "right", fontWeight: 900 }}>Closing</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={level === "cnf" ? 5 : 6} style={{ padding: 60, textAlign: "center", color: "var(--text-muted)" }}>Reconciling inventory matrices...</td></tr>
                            ) : data.length === 0 ? (
                                <tr><td colSpan={level === "cnf" ? 5 : 6} style={{ padding: 60, textAlign: "center", color: "var(--text-muted)" }}>No inventory records for this date.</td></tr>
                            ) : (
                                <>
                                    {data.map((row, idx) => (
                                        <tr key={idx}>
                                            {level !== "cnf" && <td style={{ fontWeight: 700 }}>{row.entity}</td>}
                                            <td>
                                                <div style={{ fontWeight: 800, color: "var(--primary)" }}>{row.sku}</div>
                                                <div style={{ fontSize: 10, fontWeight: 600 }}>{row.skuName}</div>
                                            </td>
                                            <td style={{ textAlign: "right", fontWeight: 700 }}>{row.opening.toLocaleString()}</td>
                                            <td style={{ textAlign: "right", fontWeight: 700, color: "var(--success)" }}>{row.received.toLocaleString()}</td>
                                            <td style={{ textAlign: "right", fontWeight: 700, color: "var(--error)" }}>{row.sold.toLocaleString()}</td>
                                            <td style={{ textAlign: "right", fontWeight: 900, background: "var(--surface-hover)" }}>{row.closing.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ background: "var(--text-main)", color: "white" }}>
                                        <td colSpan={level === "cnf" ? 1 : 2} style={{ fontWeight: 900, paddingLeft: 32 }}>AGGREGATE TOTAL</td>
                                        <td style={{ textAlign: "right", fontWeight: 900 }}>{totals.opening.toLocaleString()}</td>
                                        <td style={{ textAlign: "right", fontWeight: 900 }}>{totals.received.toLocaleString()}</td>
                                        <td style={{ textAlign: "right", fontWeight: 900 }}>{totals.sold.toLocaleString()}</td>
                                        <td style={{ textAlign: "right", fontWeight: 900, fontSize: 16 }}>{totals.closing.toLocaleString()}</td>
                                    </tr>
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </Layout>
    );
};

export default RsmStockReports;
